// Sprint 5b U5 — Jira-style data-driven subtree grouping for the Tasks
// Kanban board. Stateless, pure function: same input → same output.
//
// Rule (plan §5.4 + plan §High-Level Technical Design):
//
//   * Same-status children are nested visually inside the parent card,
//     in the parent's column.
//   * Different-status children render as standalone cards in their own
//     status column, wrapped in a parent-reference frame linking back to
//     the parent.
//   * Orphaned children (parent_task_id set but parent not in the result
//     set — e.g. parent filtered out by status[] filter) render
//     standalone with `parent: null` so the card can still display a
//     "Parent: (non disponible)" affordance instead of crashing.
//   * Same-status nested children are capped at SAME_STATUS_NESTED_CAP
//     visible; the rest are surfaced via the `overflowCount` field for
//     the card to render a "voir N de plus" disclosure.
//
// The function is a pure function of `tasks`. After every Inertia
// reload, `groupTasksForBoard(tasks)` is recomputed from scratch — no
// client-side override map, no view-session state, no persisted
// rendering decisions between drags.

import type { TaskRow, TaskStatus } from '@/types/work'

export const KANBAN_COLUMNS: TaskStatus[] = [
  'pending',
  'in_progress',
  'on_hold',
  'completed',
  'cancelled',
  'archived',
]

// Plan §10 Q7: same-status children cap of 3 visible + "voir N de plus".
export const SAME_STATUS_NESTED_CAP = 3

export interface ParentReference {
  id: number
  title: string
}

interface ParentWithNested {
  type: 'parent-with-nested'
  task: TaskRow
  // Full list of same-status children. The card slices to
  // SAME_STATUS_NESTED_CAP for the default view and exposes the rest
  // through the "voir N de plus" disclosure when the user expands.
  // (Truncating here would leave the disclosure with nothing to show.)
  nestedChildren: TaskRow[]
}

interface ChildWithParentFrame {
  type: 'child-with-parent-frame'
  task: TaskRow
  // null when the parent isn't in the current result set (filtered out
  // by status[]/priority[] etc., or simply outside the perimeter).
  parent: ParentReference | null
}

export type BoardItem = ParentWithNested | ChildWithParentFrame

export type BoardColumns = Record<TaskStatus, BoardItem[]>

function emptyColumns(): BoardColumns {
  return {
    pending: [],
    in_progress: [],
    on_hold: [],
    completed: [],
    cancelled: [],
    archived: [],
  }
}

export function groupTasksForBoard(tasks: TaskRow[]): BoardColumns {
  const byId = new Map<number, TaskRow>()
  for (const t of tasks) byId.set(t.id, t)

  const topLevel: TaskRow[] = []
  const children: TaskRow[] = []
  for (const t of tasks) {
    if (t.parent_task_id == null) topLevel.push(t)
    else children.push(t)
  }

  // Bucket children: same-status under parent (if parent is top-level
  // AND in the result set), else standalone with a parent-ref frame.
  const nestedByParentId = new Map<number, TaskRow[]>()
  const standaloneOrphans: ChildWithParentFrame[] = []

  for (const child of children) {
    const parentId = child.parent_task_id as number
    const parent = byId.get(parentId)
    const sameStatusUnderTopLevelParent =
      parent != null && parent.parent_task_id == null && parent.status === child.status

    if (sameStatusUnderTopLevelParent) {
      const list = nestedByParentId.get(parentId) ?? []
      list.push(child)
      nestedByParentId.set(parentId, list)
    } else {
      standaloneOrphans.push({
        type: 'child-with-parent-frame',
        task: child,
        parent: parent ? { id: parent.id, title: parent.title } : null,
      })
    }
  }

  const columns = emptyColumns()

  for (const parent of topLevel) {
    const all = nestedByParentId.get(parent.id) ?? []
    columns[parent.status].push({
      type: 'parent-with-nested',
      task: parent,
      nestedChildren: all,
    })
  }

  for (const orphan of standaloneOrphans) {
    columns[orphan.task.status].push(orphan)
  }

  return columns
}
